// SPEC-IN-APP-FEEDBACK-01 (D-852): 不具合・要望 を送る下からのシート。入口はランチャーのログアウトの下。
//   入力 = 急ぎ / ふつう(既定ふつう)・本文(必須)・写真 1 枚(任意)。送信は services/appFeedback.submitFeedback。
//   圏外は「送れない」を出して下書きを残す(この端末の localStorage。写真は残らない)。
//   D-889: 下に「送った要望」(自分が送った分と、対応済みなら返信)を出す。開いたら返信を既読にする。
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  submitFeedback, loadFeedbackDraft, saveFeedbackDraft, clearFeedbackDraft,
  listMyFeedback, markFeedbackSeen, unseenReplyIds,
  URGENCY, FEEDBACK_MAX_LEN,
} from '../services/appFeedback'
import DateTime from '../shared/ui/DateTime'

const EXCERPT_LEN = 80

/**
 * D-889「送った要望」: 自分が送った 不具合・要望 の一覧(新しい順・最大 20 件)と、対応済みなら返信。
 *   シートを開くたびに読み直す。対応済みで未読の行があるときは最初から開いた状態にする。
 *   ★この欄を開いた時点で、そのとき表示している 対応済み・未読 の行だけを既読にする(fn_feedback_mark_seen)。
 *     「新着」の印はシートを閉じるまで残す(既読にした直後に印が消えると、どれが新しいか分からなくなる)。
 */
function MyFeedbackSection({ staffId, onRepliesSeen }) {
  const [rows, setRows] = useState(null) // null = 読み込み中
  const [error, setError] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [newIds, setNewIds] = useState(() => new Set())
  const markedRef = useRef(new Set())

  useEffect(() => {
    let alive = true
    listMyFeedback(staffId).then(({ data, error: err }) => {
      if (!alive) return
      setRows(data)
      setError(!!err)
      const ids = unseenReplyIds(data)
      setNewIds(new Set(ids))
      if (ids.length > 0) setExpanded(true)
    })
    return () => { alive = false }
  }, [staffId])

  // 開いている間に、表示中の 対応済み・未読 を既読にする(同じ id は 2 回送らない)。
  useEffect(() => {
    if (!expanded || !rows) return
    const ids = unseenReplyIds(rows).filter(id => !markedRef.current.has(id))
    if (ids.length === 0) return
    ids.forEach(id => markedRef.current.add(id))
    markFeedbackSeen(ids).then(r => {
      if (r.status === 'ok') onRepliesSeen?.()
    })
  }, [expanded, rows, onRepliesSeen])

  const newCount = newIds.size

  return (
    <div className="border-t border-border pt-3" data-testid="feedback-mine">
      <button type="button" data-testid="feedback-mine-toggle" aria-expanded={expanded}
        onClick={() => setExpanded(v => !v)}
        className="w-full min-h-[44px] flex items-center justify-between gap-2 text-left">
        <span className="text-lg font-bold">
          送った要望
          {newCount > 0 && (
            <span data-testid="feedback-mine-new-count"
              className="ml-2 inline-block rounded-full bg-red-600 px-2 text-sm text-white align-middle">
              返信 {newCount}
            </span>
          )}
        </span>
        <span className="text-base text-muted">{expanded ? '閉じる' : '見る'}</span>
      </button>

      {expanded && (
        <div className="mt-2 space-y-2" data-testid="feedback-mine-list">
          {rows === null ? (
            <p className="text-base text-muted py-2">読み込み中…</p>
          ) : error ? (
            <p data-testid="feedback-mine-error" className="text-base text-red-300 py-2">送った要望を読み込めませんでした</p>
          ) : rows.length === 0 ? (
            <p data-testid="feedback-mine-empty" className="text-base text-muted py-2">まだ送った要望はありません</p>
          ) : rows.map(r => {
            const handled = !!r.handled_at
            const excerpt = r.body.length > EXCERPT_LEN ? `${r.body.slice(0, EXCERPT_LEN)}…` : r.body
            return (
              <div key={r.id} data-testid={`feedback-mine-row-${r.id}`}
                className={`rounded-xl border p-3 ${newIds.has(r.id) ? 'border-emerald-500' : 'border-border'} bg-bg`}>
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span data-testid={`feedback-mine-status-${r.id}`}
                    className={`font-bold ${handled ? 'text-emerald-300' : 'text-amber-300'}`}>
                    {handled ? '対応済み' : '未対応'}
                  </span>
                  {newIds.has(r.id) && (
                    <span data-testid={`feedback-mine-new-${r.id}`} className="rounded bg-red-600 px-1.5 text-xs font-bold text-white">新着</span>
                  )}
                  {r.urgency === URGENCY.urgent && <span className="text-red-300">急ぎ</span>}
                  {r.has_photo && <span className="text-muted">写真あり</span>}
                  <span className="ml-auto text-muted"><DateTime value={r.created_at} format="full" /></span>
                </div>
                <p className="mt-1 text-base whitespace-pre-wrap break-words">{excerpt}</p>
                {handled && r.response_note && (
                  <div data-testid={`feedback-mine-reply-${r.id}`}
                    className="mt-2 rounded-lg bg-emerald-500/10 px-3 py-2 text-base whitespace-pre-wrap break-words">
                    <span className="block text-sm font-bold text-emerald-300">返信</span>
                    {r.response_note}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function FeedbackSheet({ open, onClose, staffId, storeCode = null, onRepliesSeen }) {
  const location = useLocation()
  const [body, setBody] = useState('')
  const [urgency, setUrgency] = useState(URGENCY.normal)
  const [photo, setPhoto] = useState(null)
  const [requestId, setRequestId] = useState(null)
  const [phase, setPhase] = useState('edit') // edit | sending | done
  const [notice, setNotice] = useState(null) // { kind: 'offline'|'error', text }
  const fileRef = useRef(null)

  // 開いたときに下書きを戻す。
  useEffect(() => {
    if (!open) return
    const d = loadFeedbackDraft()
    setBody(d?.body ?? '')
    setUrgency(d?.urgency ?? URGENCY.normal)
    setRequestId(d?.requestId ?? null)
    setPhoto(null)
    setPhase('edit')
    setNotice(null)
  }, [open])

  if (!open) return null

  const sending = phase === 'sending'
  const canSend = body.trim().length > 0 && !sending

  function changeBody(v) {
    setBody(v)
    setRequestId(null) // 中身が変わったら別の報告として送る
    saveFeedbackDraft({ body: v, urgency, requestId: null })
  }
  function changeUrgency(u) {
    setUrgency(u)
    saveFeedbackDraft({ body, urgency: u, requestId })
  }

  async function handleSend() {
    if (!canSend) return
    setPhase('sending')
    setNotice(null)
    const r = await submitFeedback({
      body, urgency, photoFile: photo, staffId, pathname: location.pathname, storeCode, requestId: requestId || undefined,
    })
    if (r.status === 'ok') {
      clearFeedbackDraft()
      setPhase('done')
      return
    }
    setRequestId(r.requestId)
    saveFeedbackDraft({ body, urgency, requestId: r.requestId })
    setPhase('edit')
    if (r.status === 'offline') {
      setNotice({ kind: 'offline', text: '圏外のため送れませんでした。書いた内容は残っています。電波のある所でもう一度「送る」を押してください。' })
    } else {
      setNotice({ kind: 'error', text: `${r.message || '送れませんでした'}。書いた内容は残っています。` })
    }
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex flex-col justify-end bg-black/60"
      data-testid="feedback-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="不具合・要望を送る"
    >
      <button type="button" aria-label="閉じる" className="flex-1 min-h-[44px]" onClick={sending ? undefined : onClose} />
      <div className="max-h-[88%] overflow-y-auto rounded-t-2xl bg-surface border-t border-border px-4 pt-4 pb-6 text-text">
        {phase === 'done' ? (
          <div data-testid="feedback-done" className="py-4 text-center space-y-4">
            <p className="text-2xl font-bold">送りました</p>
            <p className="text-lg text-muted">ありがとうございます。確認して対応します。</p>
            <button type="button" onClick={onClose} data-testid="feedback-close"
              className="w-full h-12 rounded-xl bg-slate-700 text-white text-lg font-bold">閉じる</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xl font-bold">不具合・要望を送る</p>
              <button type="button" onClick={onClose} disabled={sending} data-testid="feedback-cancel"
                className="h-11 px-3 text-base text-muted disabled:opacity-40">閉じる</button>
            </div>

            <div className="grid grid-cols-2 gap-2" role="group" aria-label="急ぎかどうか">
              <button type="button" data-testid="feedback-urgency-normal" aria-pressed={urgency === URGENCY.normal}
                onClick={() => changeUrgency(URGENCY.normal)}
                className={`h-12 rounded-xl border text-lg font-bold ${urgency === URGENCY.normal ? 'bg-blue-600 border-blue-600 text-white' : 'border-border text-muted'}`}>
                ふつう
              </button>
              <button type="button" data-testid="feedback-urgency-urgent" aria-pressed={urgency === URGENCY.urgent}
                onClick={() => changeUrgency(URGENCY.urgent)}
                className={`h-12 rounded-xl border text-lg font-bold ${urgency === URGENCY.urgent ? 'bg-red-600 border-red-600 text-white' : 'border-border text-muted'}`}>
                急ぎ
              </button>
            </div>

            <label className="block">
              <span className="text-base text-muted">どの画面で・何が・どうなったか / こうしてほしい</span>
              <textarea
                data-testid="feedback-body"
                value={body}
                maxLength={FEEDBACK_MAX_LEN}
                onChange={e => changeBody(e.target.value)}
                rows={5}
                className="mt-1 w-full rounded-xl border border-border bg-bg p-3 text-lg text-text"
              />
            </label>

            <div className="flex items-center gap-2">
              {/* iOS Safari: display:none は input 自身に付ける(ui.md) */}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="feedback-photo-input"
                onChange={e => { setPhoto(e.target.files?.[0] ?? null); e.target.value = '' }} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={sending} data-testid="feedback-photo-pick"
                className="h-11 px-4 rounded-xl border border-border text-base">
                {photo ? '写真を替える' : '写真を付ける(任意)'}
              </button>
              {photo && (
                <>
                  <span className="text-sm text-muted truncate flex-1" data-testid="feedback-photo-name">写真 1 枚</span>
                  <button type="button" onClick={() => setPhoto(null)} disabled={sending}
                    className="h-11 px-3 text-base text-muted">外す</button>
                </>
              )}
            </div>

            {notice && (
              <p data-testid={`feedback-notice-${notice.kind}`}
                className={`rounded-xl px-3 py-2 text-base ${notice.kind === 'offline' ? 'bg-amber-500/15 text-amber-200' : 'bg-red-500/15 text-red-200'}`}>
                {notice.text}
              </p>
            )}

            <button type="button" onClick={handleSend} disabled={!canSend} data-testid="feedback-send"
              className="w-full h-12 rounded-xl bg-emerald-600 text-white text-lg font-bold disabled:opacity-40">
              {sending ? '送信中…' : '送る'}
            </button>

            {staffId && <MyFeedbackSection staffId={staffId} onRepliesSeen={onRepliesSeen} />}
          </div>
        )}
      </div>
    </div>
  )
}
